import { RawAvatar, AvatarSize } from "@components/Avatar";
import {
  Button,
  ButtonTargetKind,
  ButtonRole,
  ButtonSize,
} from "@components/Button";
import { css } from "@emotion/react";

import { ReactComponent as PlaceholderIllustration } from "src/assets/illustrations/nonprofit.svg";
import { useNonprofit } from "src/context/NonprofitsContext/hooks";
import { NonprofitFetchStatus } from "src/context/NonprofitsContext/types";
import { colorCssVars } from "src/theme/color";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { textSizeCss, FontWeight } from "src/theme/text";

interface FundraiserNonprofitslistProps {
  nonprofitIds: string[];
  onRemove?: (id: string) => void;
}

export const FundraiserNonprofitsList = ({
  nonprofitIds,
  onRemove,
}: FundraiserNonprofitslistProps) => {
  if (!nonprofitIds || nonprofitIds.length === 0) {
    return null;
  }

  return (
    <div css={spacing.xs}>
      <p
        css={[
          textSizeCss.xs,
          {
            color: `var(${colorCssVars.text.secondary})`,
            marginBottom: spacing.s,
          },
        ]}
      >
        This fundraiser supports the following nonprofits:
      </p>
      <ul css={verticalStackCss.s}>
        {nonprofitIds.map((id) => (
          <ListItem key={id} nonprofitId={id} onRemove={onRemove} />
        ))}
      </ul>
    </div>
  );
};

interface ListItemProps {
  nonprofitId: string;
  onRemove?: (id: string) => void;
}

const ListItem = ({ nonprofitId, onRemove }: ListItemProps) => {
  const nonprofit = useNonprofit({ id: nonprofitId });

  if (nonprofit === NonprofitFetchStatus.NONPROFIT_NOT_FOUND) {
    return null;
  }

  if (nonprofit === NonprofitFetchStatus.FETCHING_NONPROFIT) {
    return (
      <li
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing.xs};
          position: relative;
        `}
      >
        <RawAvatar
          size={AvatarSize.XX_SMALL}
          placeholder={<PlaceholderIllustration />}
        />
        <span
          css={[
            textSizeCss.xs,
            { fontWeight: FontWeight.MEDIUM, lineHeight: "1.25" },
          ]}
        >
          Loading...
        </span>
      </li>
    );
  }

  return (
    <li
      key={nonprofit.id}
      css={css`
        display: flex;
        align-items: center;
        gap: ${spacing.xs};
        position: relative;
      `}
    >
      <RawAvatar
        size={AvatarSize.XX_SMALL}
        cloudinaryId={nonprofit.logoCloudinaryId}
        placeholder={<PlaceholderIllustration />}
      />
      <span
        css={[
          textSizeCss.xs,
          { fontWeight: FontWeight.MEDIUM, lineHeight: "1.25" },
        ]}
      >
        {nonprofit.name}
      </span>
      {onRemove && (
        <Button
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: () => onRemove(nonprofit.id),
          }}
          data-tname="remove-nonprofit"
          role={ButtonRole.TEXT_SECONDARY}
          size={ButtonSize.SMALL}
          css={textSizeCss.xs}
        >
          Remove
        </Button>
      )}
    </li>
  );
};
